import React, { useState } from "react";
import styled from "styled-components";
import { makeStyles } from "@material-ui/core/styles";
import CommentForm from "./forms/CommentForm";
import moment from "moment";
import { Fab, Paper, ListItemText, IconButton } from "@material-ui/core";
import MaterialTable from "material-table";
import AddIcon from "@material-ui/icons/Add";
import useTable from "./useTable";
import Popup from "./Popup";

import { isEmpty } from "lodash";
import Iconify from "./Iconify";
import CommentsUtils from "../utils/comments";
import { useSelector } from "react-redux";
import Api from "../lib/api";
import ConfirmDialog from "./ConfirmDialog";
import Notification from "./Notification";

const Wrapper = styled.div`
  padding: ${(props) => props.theme.spacing(1) / 4}px
    ${(props) => props.theme.spacing(4)}px;
  flex-direction: column;
  display: flex;
  height: 100%;
`;

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: "6px 16px",
    flexGrow: 1,
  },
  form: {
    justifySelf: "self-end",
  },
}));

function Comments(props) {
  const classes = useStyles();
  const {
    comments,
    onSave,
    rReadonly = false,
    readonly = false,
    canAdd = true,
    editableNoDoctor = false,
    onCommentDeleted = null,
  } = props;
  const { authUser } = useSelector((state) => state.auth);
  const { TblContainer } = useTable();
  const [openPopup, setOpenPopup] = useState(false);
  const [confirmDialogLoading, setConfirmDialogLoading] = useState(false);
  const [commentForEdit, setCommentForEdit] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const saveComment = async (data) => {
    await onSave(data, commentForEdit);
    onClose();
  };

  const openInPopup = (item = null) => {
    setOpenPopup(true);
    setCommentForEdit(item);
  };

  const onClose = () => {
    setOpenPopup(false);
    setCommentForEdit(null);
  };

  const openConfirmDialog = (
    setConfirmDialogObj,
    onConfirmHandle,
    onConfirmParam,
    title,
    subtitle = null
  ) => {
    setConfirmDialogObj({
      isOpen: true,
      title: title,
      subTitle: subtitle ? subtitle : "You can't undo this operation",
      onConfirm: () => onConfirmHandle(onConfirmParam),
    });
  };

  const closeConfirmDialog = (setConfirmDialogObj, confirmDialogObj) => {
    setConfirmDialogObj({
      ...confirmDialogObj,
      isOpen: false,
    });
  };

  const deleteComment = async (id) => {
    try {
      setConfirmDialogLoading(true);
      await Api.deleteComment(id);

      if (typeof onCommentDeleted == "function") {
        onCommentDeleted(id);
      }
      setNotify({
        isOpen: true,
        message: "Deleted successfully!",
        type: "success",
      });
    } catch (e) {
      setNotify({
        isOpen: true,
        message: e?.data?.message || "Something went wrong!",
        type: "error",
      });
    } finally {
      setConfirmDialogLoading(false);
      closeConfirmDialog(setConfirmDialog, confirmDialog);
    }
  };

  const onClickOnDelete = (id) => {
    openConfirmDialog(
      setConfirmDialog,
      deleteComment,
      id,
      "Are you sure to delete the comment?"
    );
  };

  const dataMaterialTable = comments.map((o) => ({ ...o }));
  const headCellsMaterial = [
    { field: "comment", title: "Comment" },
    {
      field: "user_id",
      title: "Created By",
      render: (rowData) => {
        return rowData.user && !isEmpty(rowData.user.data) ? (
          <ListItemText
            primary={`${rowData?.user?.data?.title}`}
            secondary={`${rowData?.user?.data?.building_name} - ${rowData?.user?.data?.department_name}`}
          />
        ) : (
          ""
        );
      },
    },
    {
      field: "created_at",
      title: "Created",
      render: (rowData) => {
        return new moment(rowData.created_at).format("YYYY-MM-DD HH:mm:ss");
      },

      customSort: (a, b) =>
        new Date(
          moment(a.created_at).utc().format("YYYY-MM-DD HH:mm:ss")
        ).getTime() -
        new Date(
          moment(b.created_at).utc().format("YYYY-MM-DD HH:mm:ss")
        ).getTime(),
    },
  ];

  return (
    <Wrapper>
      <Paper elevation={1} className={classes.paper}>
        <TblContainer>
          <MaterialTable
            columns={headCellsMaterial}
            data={dataMaterialTable}
            options={{
              pageSize: 10, // make initial page size
              emptyRowsWhenPaging: false, //to make page size fix in case of less data rows
              actionsColumnIndex: -1,
              sorting: true,
            }}
            actions={[
              {
                icon: () => (
                  <Fab color="primary" aria-label="add">
                    <AddIcon />
                  </Fab>
                ),
                tooltip: "Attach document",
                disabled: !canAdd || readonly,
                hidden: !canAdd || readonly,
                isFreeAction: true,
                onClick: (event) => {
                  openInPopup();
                },
              },
              {
                action: (rowData) => {
                  return {
                    icon: () => (
                      <IconButton color="primary" aria-label="add">
                        <Iconify
                          icon={"ic:outline-edit"}
                          width={22}
                          height={22}
                        />
                      </IconButton>
                    ),
                    tooltip: "Edit",
                    hidden: !CommentsUtils.userCanEditComment(
                      authUser,
                      rowData
                    ),
                    onClick: (event) => {
                      openInPopup(rowData);
                    },
                    position: "row",
                  };
                },
              },
              {
                action: (rowData) => {
                  return {
                    icon: () => (
                      <IconButton color="primary" aria-label="add">
                        <Iconify
                          style={{ color: "red" }}
                          icon={"mdi:trash"}
                          width={22}
                          height={22}
                        />
                      </IconButton>
                    ),
                    tooltip: "Delete",
                    hidden: !CommentsUtils.userCanDeleteComment(
                      authUser,
                      rowData
                    ),
                    onClick: (event) => {
                      onClickOnDelete(rowData.id);
                    },
                    position: "row",
                  };
                },
              },
            ]}
            title="Comments"
          />
        </TblContainer>
      </Paper>

      <Popup
        title={commentForEdit? "Edit comment" :"Add comment"}
        fullWidth={true}
        openPopup={openPopup}
        onClose={onClose}
      >
        <CommentForm
          commentForEdit={commentForEdit}
          saveComment={saveComment}
          className={classes.form}
          canAdd={canAdd}
          readonly={readonly}
          //rReadonly={rReadonly}
          editableNoDoctor={editableNoDoctor}
        />
      </Popup>
      <ConfirmDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
        confirmLoading={confirmDialogLoading}
      />
      <Notification notify={notify} setNotify={setNotify} />
    </Wrapper>
  );
}

export default Comments;
